import { EventsPerDayType } from '@/components/MyPlanStep/WeeklyCalendar';
import { COMPLETE, KNOWN_RECURRENCE_FREQUENCIES } from '@/constants/action';
import { WEEKDAYS_MONDAY_FIRST } from '@/constants/calendar';
import {
  CustomRecurrenceData,
  EventPayload,
  RecurrenceData,
  RecurrenceRule,
  UpdateEventPayload,
} from '@/gql/events/types';
import { Action } from '@/types/actions';
import { MyEvent } from '@/types/calendar';
import { differenceInSeconds, format, isBefore } from 'date-fns';

import { capitalizeFirstLetter, getSecondsFromDuration, humanDuration } from '.';

export function convertToMinutes(timeString?: string | null) {
  const hms = (timeString || '00:00:00').split(':');
  return Math.ceil(parseInt(hms[2]) / 60) + parseInt(hms[1]) + parseInt(hms[0]) * 60;
}

const longFormatDistanceLocaleSingular = {
  xSeconds: '{{count}} second',
  xMinutes: '{{count}} minute',
  xHours: '{{count}} hour',
  xDays: '{{count}} day',
  xWeeks: '{{count}} week',
  xMonths: '{{count}} month',
  xYears: '{{count}} year',
};

const longFormatDistanceLocalePlural = {
  xSeconds: '{{count}} seconds',
  xMinutes: '{{count}} minutes',
  xHours: '{{count}} hours',
  xDays: '{{count}} days',
  xWeeks: '{{count}} weeks',
  xMonths: '{{count}} months',
  xYears: '{{count}} years',
};

type Rule = {
  frequency?: string;
  [key: string]: any;
};

type Recurrence = {
  rules: Rule[];
  exceptions?: {
    add?: { date?: string; [key: string]: any }[];
    [key: string]: any;
  };
};

export const longEnLocale = {
  formatDistance: (token: keyof typeof longFormatDistanceLocaleSingular, count: string) => {
    if (parseInt(count) === 1) {
      return longFormatDistanceLocaleSingular[token].replace('{{count}}', count);
    }

    return longFormatDistanceLocalePlural[token].replace('{{count}}', count);
  },
};

export function getTotalDurationOfStarredActions(action: Action[]) {
  const minutes = action?.reduce((acc, curr) => {
    if (curr?.dateOfStarring) {
      return acc + getSecondsFromDuration(curr.duration || '00:00:00');
    }
    return acc;
  }, 0);

  if (minutes === 0) {
    return '0h';
  }

  return humanDuration(minutes);
}

export function calculateTotalDurationOfHomeEvents(events: MyEvent[]) {
  const totalDuration = events.reduce((acc, event) => {
    const seconds = differenceInSeconds(event.end, event.start);
    return acc + seconds;
  }, 0);

  if (totalDuration === 0) {
    return '0h';
  }

  return humanDuration(totalDuration);
}

export function addDurationToTime(time: string, duration: string): string {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  const [durationHours, durationMinutes, durationSeconds] = duration.split(':').map(Number);

  const totalHours = hours + durationHours;
  const totalMinutes = minutes + durationMinutes;
  const totalSeconds = seconds + durationSeconds;

  // Handle overflow from seconds to minutes and minutes to hours
  let carryMinutes = Math.floor(totalSeconds / 60);
  let carryHours = Math.floor((totalMinutes + carryMinutes) / 60);

  const finalHours = totalHours + carryHours;
  const finalMinutes = (totalMinutes + carryMinutes) % 60;
  const finalSeconds = totalSeconds % 60;

  // Ensure leading zeros in the output
  const formattedHours = String(finalHours).padStart(2, '0');
  const formattedMinutes = String(finalMinutes).padStart(2, '0');
  const formattedSeconds = String(finalSeconds).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export function extractTimeFromDate(date: Date | null): string {
  if (!date) {
    return '';
  }

  return format(date, 'HH:mma');
}

export function isCompleted(eventStatus: string) {
  return eventStatus === COMPLETE ? true : false;
}

export function getCalendarDayTextColor(day: Date) {
  if (isBefore(day, new Date())) {
    return 'gray.400';
  }

  return 'text-primary';
}

// Get the current time
const currentTime: Date = new Date(); // Assuming this is in the same timezone as event times

export function isBetweenEventTimes(start: Date, end: Date) {
  const eventStartTime = new Date(start);
  const eventEndTime = new Date(end);

  return currentTime >= eventStartTime && currentTime <= eventEndTime;
}

export function mergeDurations(durations: string[]): string {
  const totalSeconds = durations.reduce((acc, duration) => {
    const [hours, minutes, seconds] = duration.split(':').map(Number);
    return acc + hours * 3600 + minutes * 60 + seconds;
  }, 0);

  return humanDuration(totalSeconds);
}

export const getTotalDuration = (events: Record<string, EventsPerDayType>, starredOnly = false) => {
  const eventsValues = Object.values(events);

  const durations = eventsValues.reduce((acc, { events }) => {
    const durationStrings = events.map(({ action }) => {
      if (action.duration && !starredOnly) {
        return action.duration;
      }

      if (action.duration && starredOnly && !!action.dateOfStarring) {
        return action.duration;
      }

      return '00:00:00';
    });
    return [...acc, ...durationStrings];
  }, [] as string[]);

  return mergeDurations(durations);
};
export function isValidRecurrenceRuleFrequency(normalizedFrequency: string): boolean {
  return KNOWN_RECURRENCE_FREQUENCIES.has(normalizedFrequency);
}

export const validateRecurrence = (recurrence: Recurrence | null | undefined): boolean => {
  if (!recurrence || typeof recurrence !== 'object') {
    return false;
  }

  if (!Array.isArray(recurrence.rules) || recurrence.rules.length === 0) {
    return false;
  }

  return recurrence.rules.every((rule) => {
    if (!rule || typeof rule !== 'object' || !('frequency' in rule)) {
      return false;
    }
    const raw = (rule as RecurrenceRule).frequency;
    if (typeof raw !== 'string') {
      return false;
    }
    const normalized = normalizeRecurrenceRuleFrequency(raw);
    if (!normalized) {
      return false;
    }
    return isValidRecurrenceRuleFrequency(normalized);
  });
};

/** Actions that "move all to next interval" can reschedule (linked recurring events are not moved in bulk). */
export function filterActionsMovableByBulkIntervalMove(actions: Action[]): Action[] {
  return actions.filter((action) => !validateRecurrence(action?.event?.recurrence));
}

export function hasBulkIntervalMoveTargets(actions: Action[]): boolean {
  return filterActionsMovableByBulkIntervalMove(actions).length > 0;
}

/** Some actions would move and at least one recurring action would stay — use clearer confirm copy. */
export function bulkIntervalMoveExcludesSomeRecurring(actions: Action[]): boolean {
  if (!actions.length) {
    return false;
  }
  const movable = filterActionsMovableByBulkIntervalMove(actions);
  return movable.length > 0 && movable.length < actions.length;
}

/** API + UI: recurrence rule frequency is always lowercase (daily | weekly | monthly | yearly). */
export function normalizeRecurrenceRuleFrequency(value: string | null | undefined): string {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
}

/** Ensure every rule sent to create/update event mutations has lowercase frequency. */
export function normalizeRecurrenceDataForApi(
  recurrence: RecurrenceData | null | undefined,
): RecurrenceData | undefined {
  if (!recurrence || !Array.isArray(recurrence.rules)) {
    return recurrence ?? undefined;
  }
  return {
    ...recurrence,
    rules: recurrence.rules.map((rule) => {
      if (!rule || typeof rule !== 'object') return rule;
      const r = rule as RecurrenceRule;
      if (typeof r.frequency !== 'string') return rule;
      return { ...r, frequency: normalizeRecurrenceRuleFrequency(r.frequency) };
    }),
  };
}

export function sanitizeRecurrenceForApi(
  recurrence: RecurrenceData | null | undefined,
): RecurrenceData | null | undefined {
  if (recurrence == null) {
    return undefined;
  }
  const normalized = normalizeRecurrenceDataForApi(recurrence);
  if (!normalized || !validateRecurrence(normalized)) {
    return null;
  }
  return normalized;
}

export function normalizeEventPayloadForRecurrence<T extends EventPayload | UpdateEventPayload>(payload: T): T {
  type MutableEventFields = {
    recurrence?: RecurrenceData | null;
    start?: string | null;
    end?: string | null;
  };
  const next = { ...payload } as T & MutableEventFields;

  const recurrenceKeyPresent = Object.prototype.hasOwnProperty.call(payload, 'recurrence');
  const isCreatingEvent = 'actionId' in payload;

  let strippedInvalidRecurrenceOnUpdate = false;

  if (recurrenceKeyPresent) {
    if (payload.recurrence != null) {
      const sanitized = sanitizeRecurrenceForApi(payload.recurrence);
      if (sanitized == null) {
        if (isCreatingEvent) {
          (next as MutableEventFields).recurrence = null;
        } else {
          strippedInvalidRecurrenceOnUpdate = true;
          delete (next as MutableEventFields).recurrence;
          delete (next as MutableEventFields).start;
          delete (next as MutableEventFields).end;
        }
      } else {
        (next as MutableEventFields).recurrence = sanitized;
      }
    } else {
      (next as MutableEventFields).recurrence = null;
    }
  }

  const isRecurring = validateRecurrence((next as MutableEventFields).recurrence ?? undefined);

  if (!strippedInvalidRecurrenceOnUpdate && !isRecurring && (isCreatingEvent || recurrenceKeyPresent)) {
    (next as MutableEventFields).start = null;
    (next as MutableEventFields).end = null;
  }

  return next as T;
}

/**
 * UPDATE_EVENT does a full jsonb replace of recurrence, and most flows rebuild it from the
 * client cache without exceptions.custom — silently erasing per-occurrence completions
 * recorded on the server. Re-apply every server-side completion the outgoing payload doesn't
 * explicitly set, so occurrence moves/deletes/edits can't wipe done-marks. The payload always
 * wins where it says something (an explicit progressStatus is never overwritten), and nothing
 * is ever removed from it. Known gap: dateOfStarring is not preserved the same way — starring
 * has its own intentional-removal flows and is recoverable, completions are the reported loss.
 */
export function mergeServerCompletionsIntoRecurrence(
  payloadRecurrence: RecurrenceData,
  serverRecurrence: unknown,
): RecurrenceData {
  // Never mutate the input: pass-through call sites hand us the react-query cache's object.
  const result = structuredClone(payloadRecurrence);

  if (!serverRecurrence || typeof serverRecurrence !== 'object' || Array.isArray(serverRecurrence)) {
    return result;
  }
  const serverCustom = (serverRecurrence as RecurrenceData).exceptions?.custom;
  if (!serverCustom || typeof serverCustom !== 'object' || Array.isArray(serverCustom)) {
    return result;
  }

  for (const [dateKey, entry] of Object.entries(serverCustom)) {
    if (!entry || typeof entry !== 'object' || entry.progressStatus !== COMPLETE) {
      continue;
    }
    const existing = result.exceptions?.custom?.[dateKey];
    if (existing?.progressStatus != null) {
      continue;
    }
    result.exceptions ??= {};
    result.exceptions.custom ??= {};
    result.exceptions.custom[dateKey] = { ...existing, progressStatus: COMPLETE };
  }

  return result;
}

export function getValueInRecurrenceRule(
  recurrence: Recurrence | null | undefined,
  key: keyof RecurrenceRule,
): string | null {
  if (!recurrence || typeof recurrence !== 'object' || !Array.isArray(recurrence.rules)) {
    return null;
  }

  const ruleWithKey = recurrence.rules.find((rule) => rule && typeof rule === 'object' && key in rule && rule[key]);

  const raw = ruleWithKey ? String(ruleWithKey[key]) : null;
  if (key === 'frequency' && raw != null) {
    const n = normalizeRecurrenceRuleFrequency(raw);
    return n === '' ? null : n;
  }
  return raw;
}

export const getFrequency = (recurrenceType: string, customRecurrence?: string): string => {
  const isWeekdayOnly = isEveryWeekdayLabel(recurrenceType);

  const parsedRecurrenceType = parseRecurrenceFromLabel(recurrenceType);

  const customRecurrenceValues: Record<string, string> = {
    week: 'weekly',
    day: 'daily',
    month: 'monthly',
    year: 'yearly',
  };

  let raw: string;
  if (isWeekdayOnly) {
    raw = 'weekly';
  } else if (customRecurrence) {
    const singular = customRecurrence.toLowerCase().replace(/s$/, '');
    raw = customRecurrenceValues[singular] || parsedRecurrenceType;
  } else {
    raw = parsedRecurrenceType;
  }

  if (raw == null || raw === '') {
    return '';
  }
  return normalizeRecurrenceRuleFrequency(raw);
};

export const getRepeatEveryFrequency = (recurrence: string, interval: number) => {
  const parsedRecurrence = parseRecurrenceFromLabel(recurrence);

  const repeatEveryValues: Record<string, string> = {
    weekly: interval > 1 ? 'weeks' : 'week',
    daily: interval > 1 ? 'days' : 'day',
    monthly: interval > 1 ? 'months' : 'month',
    yearly: interval > 1 ? 'years' : 'year',
  };

  return capitalizeFirstLetter(repeatEveryValues[parsedRecurrence.toLocaleLowerCase()] || parsedRecurrence);
};

export const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const dayAbbrev: Record<string, string> = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

export function formatRecurrenceChipLabel(data: CustomRecurrenceData, refDate?: Date): string {
  const interval = data.interval || 1;
  const freq = (data.selectedRecurrence || '').toLowerCase();

  if (freq === 'day') {
    return interval > 1 ? `Every ${interval} days` : 'Daily';
  }
  if (freq === 'week') {
    const days = data.selectedWeekDays?.map((d) => d.day) || [];
    if (days.length === 0 && refDate) {
      const dayName = daysOfWeek[refDate.getDay()];
      return interval > 1
        ? `Every ${interval} weeks on ${dayAbbrev[dayName.toLowerCase()] ?? dayName.slice(0, 3)}`
        : `Weekly on ${dayAbbrev[dayName.toLowerCase()] ?? dayName.slice(0, 3)}`;
    }
    const sortedDays = [...days].sort(
      (a, b) =>
        WEEKDAYS_MONDAY_FIRST.indexOf(a.toLowerCase()) - WEEKDAYS_MONDAY_FIRST.indexOf(b.toLowerCase()),
    );
    const abbrev = sortedDays
      .map((d) => dayAbbrev[d.toLowerCase()] ?? capitalizeFirstLetter(d).slice(0, 3))
      .join(', ');
    return interval > 1 ? `Every ${interval} weeks on ${abbrev}` : `Weekly on ${abbrev}`;
  }
  if (freq === 'month') {
    const date = refDate ?? undefined;
    const day = date?.getDate();
    const suffix =
      day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
    const onDate = day != null ? `the ${day}${suffix}` : '';
    return interval > 1
      ? (onDate ? `Every ${interval} months on ${onDate}` : `Every ${interval} months`)
      : (onDate ? `Monthly on ${onDate}` : 'Monthly');
  }
  if (freq === 'year') {
    const date = refDate ?? undefined;
    const onDate = date ? format(date, 'MMM d') : '';
    return interval > 1
      ? (onDate ? `Every ${interval} years on ${onDate}` : `Every ${interval} years`)
      : (onDate ? `Yearly on ${onDate}` : 'Yearly');
  }
  return '';
}

export const monthsOfYear = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** True when the label is "every weekday" in EN or ES (e.g. "Entre semana (lunes a viernes)"). */
export const isEveryWeekdayLabel = (label: string): boolean =>
  /^every weekday \(monday to friday\)$/i.test(label) ||
  /^entre semana \(lunes a viernes\)$/i.test(label);

export const parseRecurrenceFromLabel = (label: string): string => {
  if (isEveryWeekdayLabel(label)) {
    return 'weekly';
  }

  const dailyMatch = label.match(/^every (\d+) days$/i);
  if (dailyMatch) {
    return 'daily';
  }

  const weeklyMatch = label.match(/^every (\d+)? ?weeks? on (.+)$/i);
  if (weeklyMatch) {
    return 'weekly';
  }

  const weeklyOnMatch = label.match(/^weekly on (.+)$/i);
  if (weeklyOnMatch) {
    return 'weekly';
  }

  const weeklyOnEsMatch = label.match(/^semanal (?:en|el) (.+)$/i);
  if (weeklyOnEsMatch) {
    return 'weekly';
  }

  const monthlyMatch = label.match(/^every (\d+) months$/i);
  if (monthlyMatch) {
    return 'monthly';
  }

  const monthlyOnMatch = label.match(/^monthly on (\d{1,2})$/i);
  if (monthlyOnMatch) {
    return 'monthly';
  }

  const monthlyOnOrdinalMatch = label.match(/^monthly on the \d{1,2}(?:st|nd|rd|th)$/i);
  if (monthlyOnOrdinalMatch) {
    return 'monthly';
  }

  const monthlyOnDateMatch = label.match(/^every (\d+) months on [A-Za-z]{3} \d{1,2}$/i);
  if (monthlyOnDateMatch) {
    return 'monthly';
  }

  const monthlyOnOrdinalNMatch = label.match(/^every (\d+) months on the \d{1,2}(?:st|nd|rd|th)$/i);
  if (monthlyOnOrdinalNMatch) {
    return 'monthly';
  }

  const yearlyMatch = label.match(/^every (\d+) years$/i);
  if (yearlyMatch) {
    return 'yearly';
  }

  const yearlyOnNMatch = label.match(/^every (\d+) years on .+$/i);
  if (yearlyOnNMatch) {
    return 'yearly';
  }

  const yearlyOnMatch = label.match(/^yearly on (\w+) (\d{1,2})$/i);
  if (yearlyOnMatch) {
    return 'yearly';
  }

  return label;
};
